import React from 'react';
import { motion } from 'framer-motion';
import {
    FaBriefcase, FaCalendarAlt, FaBuilding, FaCheckCircle,
    FaTrophy, FaMapMarkerAlt, FaTools, FaCode, FaStar,
    FaNodeJs, FaReact, FaDatabase, FaServer, FaCloud
} from 'react-icons/fa';
import { useInView } from '../hooks/useInView';
import { fadeIn, fadeInUp } from '../utils/animations';
import { useTranslation } from 'react-i18next';



const Work: React.FC = () => {
    const { t } = useTranslation();
    const { ref, isInView } = useInView(0.1);

    const workData = [
        {
            title: t('work.jobs.freelance.title'),
            company: t('work.jobs.freelance.company'),
            location: t('work.jobs.freelance.location'),
            period: t('work.jobs.freelance.period'),
            description: t('work.jobs.freelance.description'),
            responsibilities: t('work.jobs.freelance.responsibilities', { returnObjects: true }) as string[],
            achievements: t('work.jobs.freelance.achievements', { returnObjects: true }) as string[],
            technologies: ['React', 'Node.js', 'MongoDB', 'TypeScript', 'Redis', 'Express', 'Docker', 'AWS', 'Tailwind CSS'],
        },
        {
            title: t('work.jobs.softylines.title'),
            company: t('work.jobs.softylines.company'),
            location: t('work.jobs.softylines.location'),
            period: t('work.jobs.softylines.period'),
            description: t('work.jobs.softylines.description'),
            responsibilities: t('work.jobs.softylines.responsibilities', { returnObjects: true }) as string[],
            achievements: t('work.jobs.softylines.achievements', { returnObjects: true }) as string[],
            technologies: ['React', 'TypeScript', 'Node.js', 'Express', 'AWS', 'Docker', 'MongoDB', 'Redis', 'Socket.io', 'Tailwind CSS', 'Next.js'],
        },
    ];

    const techIcons: { [key: string]: JSX.Element } = {
        "React": <FaReact className="text-blue-400" />,
        "Node.js": <FaNodeJs className="text-green-400" />,
        "MongoDB": <FaDatabase className="text-green-500" />,
        "Express": <FaServer className="text-gray-400" />,
        "TypeScript": <FaCode className="text-blue-500" />,
        "JavaScript": <FaCode className="text-yellow-400" />,
        "AWS": <FaCloud className="text-orange-400" />,
        "Redis": <FaDatabase className="text-red-500" />,
        "Socket.io": <FaServer className="text-white" />,
        "Docker": <FaCode className="text-blue-400" />,
        "Next.js": <FaReact className="text-white" />,
        "Tailwind CSS": <FaCode className="text-blue-300" />
    };

    return (
        <motion.section
            id="work"
            className="py-24 relative"
            ref={ref as any}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeIn}
        >
            {/* Animated background elements */}
            <motion.div 
                className="absolute top-1/4 right-0 w-1/3 h-1/3 bg-primary-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5"
                animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.05, 0.08, 0.05]
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    repeatType: "reverse"
                }}
            ></motion.div>
            <motion.div 
                className="absolute bottom-1/4 left-0 w-1/3 h-1/3 bg-accent-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5"
                animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.05, 0.07, 0.05]
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: 1
                }}
            ></motion.div>

            <div className="responsive-container">
                <motion.div className="text-center mb-16" variants={fadeInUp}>
                    <motion.span
                        className="inline-block px-4 py-1 glass rounded-full text-gray-300 mb-3 text-sm"
                        variants={fadeInUp}
                    >
                        {t('work.professionalPath')}
                    </motion.span>
                    <motion.h2
                        className="section-heading"
                        variants={fadeInUp}
                    >
                        {t('work.title')}
                    </motion.h2>
                    <motion.div
                        className="h-1 w-20 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full mb-8"
                        variants={fadeInUp}
                    ></motion.div>
                    <motion.p
                        className="section-subheading"
                        variants={fadeInUp}
                    >
                        {t('work.subtitle')}
                    </motion.p>
                </motion.div>

                <div className="space-y-12">
                    {workData.map((job, index) => (
                        <motion.div
                            key={index}
                            className="relative"
                            variants={fadeInUp}
                            custom={index}
                            whileHover={{ y: -5 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                                <div className="md:col-span-3">
                                    <div className="glass-card p-6 h-full">
                                        <div className="flex items-center mb-4">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center mr-3">
                                                <FaBuilding className="text-primary-400 text-xl" />
                                            </div>
                                            <h4 className="text-xl font-bold text-white">{job.company}</h4>
                                        </div>

                                        <div className="space-y-3 mb-4">
                                            <div className="flex items-center">
                                                <FaCalendarAlt className="text-accent-400 mr-3 w-5 h-5" />
                                                <span className="text-gray-300">{job.period}</span>
                                            </div>
                                            <div className="flex items-center">
                                                <FaMapMarkerAlt className="text-purple-400 mr-3 w-5 h-5" />
                                                <span className="text-gray-300">{job.location}</span>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mt-auto">
                                            <span className="text-xs text-gray-400 mb-2 w-full">{t('work.technologiesUsed')}</span>
                                            {job.technologies.slice(0, 3).map((tech, techIndex) => (
                                                <span
                                                    key={techIndex}
                                                    className="bg-gray-800 bg-opacity-80 text-gray-300 px-2 py-1 rounded-full text-xs"
                                                >
                                                    {tech}
                                                </span>
                                            ))}
                                            {job.technologies.length > 3 && (
                                                <span className="bg-gray-800 bg-opacity-80 text-gray-300 px-2 py-1 rounded-full text-xs">
                                                    +{job.technologies.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="md:col-span-9">
                                    <motion.div
                                        className="glass-card p-6"
                                        whileHover={{ 
                                            boxShadow: "0 0 20px rgba(99, 102, 241, 0.3)",
                                            borderColor: "rgba(99, 102, 241, 0.5)"
                                        }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="mb-6">
                                            <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center mr-3">
                                                    <FaBriefcase className="text-primary-400" />
                                                </div>
                                                {job.title}
                                            </h3>
                                            <p className="text-gray-300 leading-relaxed">{job.description}</p>
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                                            <motion.div 
                                                className="glass p-5 rounded-xl"
                                                whileHover={{ scale: 1.02 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <h4 className="text-lg font-semibold mb-4 text-primary-400 flex items-center">
                                                    <FaCheckCircle className="mr-2" />
                                                    {t('work.keyResponsibilities')}
                                                </h4>
                                                <ul className="space-y-3">
                                                    {job.responsibilities.map((responsibility, i) => (
                                                        <motion.li 
                                                            key={i} 
                                                            className="text-gray-300 flex items-start"
                                                            initial={{ opacity: 0, x: -10 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ duration: 0.3, delay: i * 0.1 }}
                                                        >
                                                            <div className="w-6 h-6 rounded-full bg-primary-500 bg-opacity-20 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                                                                <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                                                            </div>
                                                            <span>{responsibility}</span>
                                                        </motion.li>
                                                    ))}
                                                </ul>
                                            </motion.div>

                                            <motion.div 
                                                className="glass p-5 rounded-xl"
                                                whileHover={{ scale: 1.02 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <h4 className="text-lg font-semibold mb-4 text-accent-400 flex items-center">
                                                    <FaTrophy className="mr-2" />
                                                    {t('work.achievements')}
                                                </h4>
                                                <ul className="space-y-3">
                                                    {job.achievements.map((achievement, i) => (
                                                        <motion.li 
                                                            key={i} 
                                                            className="text-gray-300 flex items-start"
                                                            initial={{ opacity: 0, x: -10 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ duration: 0.3, delay: i * 0.1 }}
                                                        >
                                                            <div className="w-6 h-6 rounded-full bg-accent-500 bg-opacity-20 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                                                                <FaStar className="text-accent-400 text-xs" />
                                                            </div>
                                                            <span>{achievement}</span>
                                                        </motion.li>
                                                    ))}
                                                </ul>
                                            </motion.div>
                                        </div>

                                        <div>
                                            <h4 className="text-md font-semibold mb-4 text-gray-200 flex items-center">
                                                <FaTools className="mr-2" /> {t('work.technologiesUsed')}
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {job.technologies.map((tech, techIndex) => (
                                                    <motion.span
                                                        key={techIndex}
                                                        className="glass px-3 py-1 rounded-full text-sm border border-gray-700 hover:border-primary-500 transition-all duration-300 flex items-center gap-1"
                                                        whileHover={{ 
                                                            scale: 1.05, 
                                                            y: -2,
                                                            boxShadow: "0 0 10px rgba(99, 102, 241, 0.3)"
                                                        }}
                                                        whileTap={{ scale: 0.95 }}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.3, delay: techIndex * 0.05 }}
                                                    >
                                                        {techIcons[tech] || <FaCode className="text-gray-400" />}
                                                        <span className="text-gray-300">{tech}</span>
                                                    </motion.span>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </motion.section>
    );
};

export default Work;
